import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { creationProductHooks, creationProductManager } from "@/manager";
import { useToast } from "@/hooks/use-toast";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { logger } from "@/utils/logger";

export function useCreationProduct() {
  logger.info("hooks: useCreationProduct");
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useI18nNavigate();
  const toast = useToast();
  const [data, setData] = useState<CachedData>(creationProductManager.get());
  const { activeFolder } = creationProductHooks.useActiveFolder();
  const { activeProject } = creationProductHooks.useActiveProject();

  // 监听数据变化
  useEffect(() => {
    return creationProductManager.listen((e) => {
      setData(e.detail);
    });
  }, []);

  // 初始数据
  const init = async (loading = true) => {
    try {
      await creationProductManager.init(loading);
    } catch (error) {
      toast.showToastByCode(error);
    }
  };

  // 获取实时数据
  const get = () => {
    return creationProductManager.get();
  };

  // 创建新文件夹
  const createFolder = async (defaultNavigate = true) => {
    const previousPath = location.pathname;
    const previousState = location.state;
    try {
      const tempId = `${Date.now()}`;
      if (defaultNavigate) {
        const tempPath = `/creation/folder/${tempId}/`;
        // 跳转临时路径
        navigate(tempPath, {
          state: {
            id: `${creationProductManager.getFolderPrefix()}${tempId}`,
          },
        });
      }
      const folderId = await creationProductManager.newFolder(tempId, t("use_creation_product.new_folder"));
      if (defaultNavigate) {
        // 跳转真实路径
        navigate(`/creation/folder/${folderId}/`, {
          state: {
            id: `${creationProductManager.getFolderPrefix()}${tempId}`,
          },
          replace: true,
        });
      }
    } catch (error) {
      toast.showToastByCode(error);
      if (defaultNavigate) {
        // 回退路径
        navigate(previousPath, {
          state: previousState,
          replace: true,
        });
      }
    }
  };

  // 创建新项目
  const createProject = async (folder: Folder | null = null, defaultNavigate = true) => {
    const previousPath = location.pathname;
    const previousState = location.state;
    try {
      const tempId = `${Date.now()}`;
      if (defaultNavigate) {
        const tempPath = `/creation/project/${tempId}/`;
        // 跳转临时路径
        navigate(tempPath, {
          state: {
            id: `${creationProductManager.getProjectPrefix()}${tempId}`,
          },
        });
      }
      const projectId = await creationProductManager.newProject(
        tempId,
        t("use_creation_product.new_project"),
        folder
      );
      if (defaultNavigate) {
        // 跳转真实路径
        navigate(`/creation/project/${projectId}/`, {
          state: {
            id: `${creationProductManager.getProjectPrefix()}${tempId}`,
          },
          replace: true,
        });
      }
    } catch (error) {
      toast.showToastByCode(error);
      // 回退路径
      if (defaultNavigate) {
        navigate(previousPath, {
          state: previousState,
          replace: true,
        });
      }
    }
  };

  // 重命名文件夹
  const renameFolder = async (folder: Folder, name: string) => {
    if (!name) {
      toast.toast({
        title: "Rename Folder",
        description: "Folder name cannot be empty",
      });
      return;
    }
    try {
      await creationProductManager.renameFolder(folder, name);
    } catch (error) {
      toast.showToastByCode(error);
    }
  };

  // 删除文件夹
  const deleteFolder = async (folder: Folder) => {
    const data = creationProductManager.get();
    if (!data) {
      return;
    }
    const isActiveFolder = folder.id === activeFolder?.id;
    try {
      if (isActiveFolder) {
        const folders = data.folders.filter((item) => item.id !== folder.id);
        const url =
          folders.length > 0
            ? `/creation/folder/${data.folderOriginIdMap[folders[0].id]}/`
            : `/creation/product/`;
        navigate(url, {
          replace: true,
        });
      }
      await creationProductManager.deleteFolder(folder);
    } catch (error) {
      toast.showToastByCode(error);
      navigate(`/creation/folder/${data.folderOriginIdMap[folder.id]}/`, {
        replace: true,
      });
    }
  };

  // 重命名项目
  const renameProject = async (
    project: Project,
    name: string,
    folder: Folder | null = null
  ) => {
    if (!name) {
      toast.toast({
        title: "Rename Project",
        description: "Project name cannot be empty",
      });
      return;
    }
    try {
      await creationProductManager.renameProject(project, name, folder);
    } catch (error) {
      toast.showToastByCode(error);
    }
  };

  // 删除项目
  const deleteProject = async (
    project: Project,
    folder: Folder | null = null
  ) => {
    const data = creationProductManager.get();
    if (!data) {
      return;
    }
    const isActiveProject = project.id === activeProject?.id;
    try {
      if (isActiveProject) {
        const folderId = folder
          ? folder.id
          : creationProductManager.getUncategorized();
        const projects = data.projectsMap[folderId]?.filter((item) => {
          return item.id !== project.id;
        });
        if (folder && !projects?.length) {
          const path = `/creation/folder/${data.folderOriginIdMap[folderId]}/`;
          navigate(path, { replace: true });
        } else if (projects.length) {
          const path = `/creation/project/${
            data.projectOriginIdMap[projects[0].id]
          }/`;
          navigate(path, { replace: true });
        } else {
          const path = `/creation/folder/uncategorized/`;
          navigate(path, { replace: true });
        }
      }
      await creationProductManager.deleteProject(project, folder);
    } catch (error) {
      toast.showToastByCode(error);
      if (isActiveProject) {
        navigate(`/creation/project/${data.projectOriginIdMap[project.id]}/`, {
          replace: true,
        });
      }
    }
  };

  // 重新排序文件夹
  const reorderFolders = useCallback(
    async (reorderedContainerIds: string[]) => {
      try {
        await creationProductManager.reorderFolders(reorderedContainerIds);
      } catch (error) {
        toast.showToastByCode(error);
      }
    },
    [toast]
  );

  // 重新排序项目
  const reorderProjects = async (
    projectId: string,
    folderId: string,
    reorderedItemIds: string[]
  ) => {
    try {
      await creationProductManager.reorderProjects(
        projectId,
        folderId,
        reorderedItemIds
      );
    } catch (error) {
      toast.showToastByCode(error);
    }
  };

  // 切换文件夹展开状态
  const toggleFolderExpanded = (folderId: string, expanded = false) => {
    const data = creationProductManager.get();
    if (!data) {
      return;
    }
    const expandedFolders = new Set(data.expandedFolders);
    if (expanded) {
      expandedFolders.add(folderId);
    } else if (expandedFolders.has(folderId)) {
      expandedFolders.delete(folderId);
    } else {
      expandedFolders.add(folderId);
    }

    creationProductManager.update({
      teamCode: data.teamCode,
      workspaceId: data.workspaceId,
      folders: data.folders,
      projectsMap: data.projectsMap,
      folderOriginIdMap: data.folderOriginIdMap,
      projectOriginIdMap: data.projectOriginIdMap,
      expandedFolders,
    });
  };

  // 跳转文件夹列表页
  const toProduct = () => {
    navigate("/creation/product/");
  };

  // 跳转项目列表页
  const toFolder = (folder: Folder) => {
    const data = creationProductManager.get();
    if (!data) {
      return;
    }
    navigate(`/creation/folder/${data.folderOriginIdMap[folder.id]}/`);
  };

  // 跳转项目详情页
  const toProject = (project: Project) => {
    const data = creationProductManager.get();
    if (!data) {
      return;
    }
    navigate(`/creation/project/${data.projectOriginIdMap[project.id]}/`);
  };

  // 查询项目所在的文件夹
  const getProjectFolder = (project: Project): Folder | null => {
    const data = creationProductManager.get();
    if (!data) {
      return null;
    }
    const { folders, projectsMap } = data;
    // 遍历所有文件夹，查找包含该项目的文件夹
    for (const folder of folders) {
      const folderProjects = projectsMap[folder.id] || [];
      if (folderProjects.some((p) => p.id === project.id)) {
        return folder;
      }
    }

    // 检查是否在未分类中
    const uncategorizedProjects =
      projectsMap[creationProductManager.uncategorized] || [];
    if (uncategorizedProjects.some((p) => p.id === project.id)) {
      return null; // 未分类返回null
    }

    // 项目不存在
    return null;
  };

  // 获取未分类文件夹id
  const getUncategorized = () => {
    return creationProductManager.getUncategorized();
  };

  // 计算派生数据
  const folders = data?.folders || [];
  const projectsMap = data?.projectsMap || {};
  const folderOriginIdMap = data?.folderOriginIdMap || {};
  const projectOriginIdMap = data?.projectOriginIdMap || {};
  const expandedFolders = new Set(data?.expandedFolders || []);
  const timestamp = creationProductManager.getTimestamp();

  // 获取未分类项目
  const uncategorizedProjects =
    projectsMap[creationProductManager.uncategorized] || [];

  return {
    data,
    activeFolder,
    activeProject,
    folders,
    projectsMap,
    folderOriginIdMap,
    projectOriginIdMap,
    expandedFolders,
    uncategorizedProjects,
    timestamp,
    init,
    get,
    createFolder,
    createProject,
    renameFolder,
    deleteFolder,
    renameProject,
    deleteProject,
    reorderFolders,
    reorderProjects,
    toggleFolderExpanded,
    toProduct,
    toFolder,
    toProject,
    getProjectFolder,
    getUncategorized,
  };
}
